import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, SparklesIcon } from '@heroicons/react/24/outline';

const Hero = () => {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');

    const tryPlay = () => {
      const playPromise = video.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.catch(() => {});
      }
    };

    tryPlay();
    const onCanPlay = () => tryPlay();
    video.addEventListener('canplay', onCanPlay);
    return () => video.removeEventListener('canplay', onCanPlay);
  }, []);

  const fadeUp = {
    initial: { opacity: 0, y: 24 },
    animate: { opacity: 1, y: 0 },
  };

  return (
    <section
      className="relative min-h-[100svh] flex items-center overflow-hidden bg-wood-900"
      aria-label="Willkommen bei SS-craftedwithlove"
    >
      {/* Video background */}
      <div className="absolute inset-0">
        <video
          ref={videoRef}
          className="w-full h-full object-cover"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          controls={false}
          aria-hidden="true"
        >
          <source src="/movie.mp4" type="video/mp4" />
        </video>
      </div>

      {/* Gradient + Vignette Overlay */}
      <div
        className="absolute inset-0 bg-gradient-to-b from-wood-900/70 via-wood-900/55 to-wood-900/85"
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 bg-gradient-to-tr from-wood-950/40 via-transparent to-gold-500/10"
        aria-hidden="true"
      />

      {/* Subtle floating particles */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        {[...Array(8)].map((_, i) => (
          <span
            key={i}
            className="absolute block rounded-full bg-gold-300/40 blur-[1px] animate-float"
            style={{
              top: `${10 + (i * 11) % 80}%`,
              left: `${(i * 17) % 95}%`,
              width: `${4 + (i % 3) * 2}px`,
              height: `${4 + (i % 3) * 2}px`,
              animationDelay: `${i * 0.6}s`,
              animationDuration: `${5 + (i % 3)}s`,
            }}
          />
        ))}
      </div>

      <div className="container relative z-10 py-24 md:py-32 lg:py-40">
        <div className="max-w-3xl">
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 mb-6 rounded-full
                       bg-white/10 backdrop-blur-md ring-1 ring-white/20 text-cream-50/90 text-sm"
          >
            <SparklesIcon className="w-4 h-4 text-gold-300" />
            <span>Handgefertigt in Daiting · Bayern</span>
          </motion.div>

          <motion.h1
            {...fadeUp}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="font-display text-cream-50 text-balance"
            style={{ textShadow: '0 4px 24px rgba(0,0,0,0.35)' }}
          >
            <span className="block text-4xl md:text-6xl lg:text-7xl font-bold leading-[1.05]">
              Individuelle
            </span>
            <span className="block text-4xl md:text-6xl lg:text-7xl font-bold leading-[1.05] bg-clip-text text-transparent bg-gradient-to-r from-gold-200 via-gold-300 to-gold-100">
              Holzlasergravuren
            </span>
          </motion.h1>

          <motion.p
            {...fadeUp}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-6 text-cream-50/90 text-lg md:text-xl max-w-2xl text-pretty"
            style={{ textShadow: '0 2px 12px rgba(0,0,0,0.45)' }}
          >
            Handgefertigte Gravuren und personalisierte Geschenke aus Holz – mit
            Liebe zum Detail gefertigt, damit Ihre besonderen Momente unvergesslich werden.
          </motion.p>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.7, delay: 0.55 }}
            className="mt-10 flex flex-wrap gap-3"
          >
            <Link to="/leistungen" className="btn btn-primary">
              Meine Leistungen
              <ArrowRightIcon className="w-4 h-4" />
            </Link>
            <Link to="/kontakt" className="btn btn-secondary">
              Kontakt aufnehmen
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-16 grid grid-cols-3 gap-6 max-w-md"
          >
            {[
              { value: '100%', label: 'Handgefertigt' },
              { value: 'Nachhaltig', label: 'Holzauswahl' },
              { value: 'Individuell', label: 'Nach Wunsch' },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-cream-50 font-display font-semibold text-base md:text-lg">
                  {stat.value}
                </div>
                <div className="text-cream-50/60 text-xs md:text-sm uppercase tracking-wider">
                  {stat.label}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 text-cream-50/70"
        aria-hidden="true"
      >
        <span className="text-xs uppercase tracking-[0.2em]">Scroll</span>
        <span className="w-px h-10 bg-gradient-to-b from-cream-50/70 to-transparent">
          <span className="block w-px h-3 bg-gold-300 animate-pulse" />
        </span>
      </motion.div>
    </section>
  );
};

export default Hero;
